import { useState } from 'react';

interface CharacterSheet {
  id?: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  alignment?: string;
  bonds?: string[];
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
}

interface CreateSheetModalProps {
  sheet?: CharacterSheet;
  onClose: () => void;
  onSave: (sheet: Omit<CharacterSheet, 'id' | 'createdAt'>) => void;
}

export function CreateSheetModal({ sheet, onClose, onSave }: CreateSheetModalProps) {
  console.log('[CreateSheetModal] Opened with sheet:', sheet);
  console.log('[CreateSheetModal] sheet.bonds:', sheet?.bonds);

  const [name, setName] = useState(sheet?.name || '');
  const [description, setDescription] = useState(sheet?.description || '');
  const [imageUrl, setImageUrl] = useState(sheet?.imageUrl || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(sheet?.imageUrl || null);
  const [bonuses, setBonuses] = useState(sheet?.bonuses || []);
  const [maluses, setMaluses] = useState(sheet?.maluses || []);
  const [moves, setMoves] = useState(sheet?.moves || []);
  const [alignment, setAlignment] = useState(sheet?.alignment || '');
  const [bonds, setBonds] = useState<string[]>(sheet?.bonds || []);
  const [notes, setNotes] = useState(sheet?.notes || '');
  const [damage, setDamage] = useState(sheet?.damage ?? 0);
  const [armor, setArmor] = useState(sheet?.armor ?? 0);
  const [maxHP, setMaxHP] = useState(sheet?.maxHP ?? 10);
  const [level, setLevel] = useState(sheet?.level ?? 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !description.trim()) {
      alert('Name and description are required');
      return;
    }

    setIsSubmitting(true);
    setUploadError(null);

    let finalImageUrl = imageUrl;

    // Upload image if a new file was selected
    if (imageFile) {
      try {
        const formData = new FormData();
        formData.append('image', imageFile);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (data.success) {
          finalImageUrl = data.url;
        } else {
          throw new Error(data.error || 'Failed to upload image');
        }
      } catch (error: any) {
        console.error('Error uploading image:', error);
        setUploadError(error.message || 'Failed to upload image');
        setIsSubmitting(false);
        return;
      }
    }

    const dataToSave = {
      name: name.trim(),
      description: description.trim(),
      imageUrl: finalImageUrl || undefined,
      bonuses,
      maluses,
      moves,
      alignment: alignment.trim(),
      bonds: bonds.filter(b => b.trim().length > 0),
      notes: notes.trim(),
      damage,
      armor,
      maxHP,
      level,
    };
    console.log('[CreateSheetModal] Saving sheet with data:', dataToSave);
    await onSave(dataToSave);
    setIsSubmitting(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError('File must be an image');
        return;
      }

      setImageFile(file);
      setUploadError(null);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl('');
  };

  // Bonus management
  const addBonus = () => {
    setBonuses([...bonuses, { attribute: '', value: 1 }]);
  };

  const updateBonus = (index: number, field: 'attribute' | 'value', value: string | number) => {
    const updated = [...bonuses];
    updated[index] = { ...updated[index], [field]: value };
    setBonuses(updated);
  };

  const removeBonus = (index: number) => {
    setBonuses(bonuses.filter((_, i) => i !== index));
  };

  // Malus management
  const addMalus = () => {
    setMaluses([...maluses, { attribute: '', value: -1 }]);
  };

  const updateMalus = (index: number, field: 'attribute' | 'value', value: string | number) => {
    const updated = [...maluses];
    updated[index] = { ...updated[index], [field]: value };
    setMaluses(updated);
  };

  const removeMalus = (index: number) => {
    setMaluses(maluses.filter((_, i) => i !== index));
  };

  // Move management
  const addMove = () => {
    setMoves([...moves, { name: '', description: '' }]);
  };

  const updateMove = (index: number, field: 'name' | 'description', value: string) => {
    const updated = [...moves];
    updated[index] = { ...updated[index], [field]: value };
    setMoves(updated);
  };

  const removeMove = (index: number) => {
    setMoves(moves.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex flex-col">
      {/* Header fisso */}
      <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 p-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">
            {sheet ? 'Edit Character Sheet' : 'Create Character Sheet'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition text-2xl"
          >
            ×
          </button>
        </div>
      </div>

      {/* Contenuto scrollabile */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto bg-gray-800 rounded-xl shadow-2xl border border-gray-700">
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Character Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Paladino, Barbaro"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                required
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Character Image (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer"
              />
              {uploadError && (
                <p className="text-red-400 text-xs mt-1">{uploadError}</p>
              )}
            </div>
          </div>

          {/* Image Preview */}
          {imagePreview && (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg border border-gray-600"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Flavour text describing the character class..."
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              required
            />
          </div>

          {/* Combat Stats */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Stats Combattimento
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Danno</label>
                <input
                  type="number"
                  value={damage}
                  onChange={(e) => setDamage(parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Armatura</label>
                <input
                  type="number"
                  value={armor}
                  onChange={(e) => setArmor(parseInt(e.target.value) || 0)}
                  min={0}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">PF Max</label>
                <input
                  type="number"
                  value={maxHP}
                  onChange={(e) => setMaxHP(parseInt(e.target.value) || 0)}
                  min={1}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Livello</label>
                <input
                  type="number"
                  value={level}
                  onChange={(e) => setLevel(parseInt(e.target.value) || 1)}
                  min={1}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Bonuses */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-green-400">BONUSES</label>
              <button
                type="button"
                onClick={addBonus}
                className="text-green-400 hover:text-green-300 text-sm font-semibold"
              >
                + Add Bonus
              </button>
            </div>
            <div className="space-y-2">
              {bonuses.map((bonus, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={bonus.attribute}
                    onChange={(e) => updateBonus(idx, 'attribute', e.target.value)}
                    placeholder="Attribute (e.g., FOR, DES)"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => updateBonus(idx, 'value', parseInt(e.target.value))}
                    className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeBonus(idx)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Maluses */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-red-400">MALUSES</label>
              <button
                type="button"
                onClick={addMalus}
                className="text-red-400 hover:text-red-300 text-sm font-semibold"
              >
                + Add Malus
              </button>
            </div>
            <div className="space-y-2">
              {maluses.map((malus, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={malus.attribute}
                    onChange={(e) => updateMalus(idx, 'attribute', e.target.value)}
                    placeholder="Attribute (e.g., INT, CAR)"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="number"
                    value={malus.value}
                    onChange={(e) => updateMalus(idx, 'value', parseInt(e.target.value))}
                    className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeMalus(idx)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Moves */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-blue-400">SPECIAL MOVES</label>
              <button
                type="button"
                onClick={addMove}
                className="text-blue-400 hover:text-blue-300 text-sm font-semibold"
              >
                + Add Move
              </button>
            </div>
            <div className="space-y-3">
              {moves.map((move, idx) => (
                <div key={idx} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={move.name}
                      onChange={(e) => updateMove(idx, 'name', e.target.value)}
                      placeholder="Move name (e.g., Smite Evil)"
                      className="flex-1 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeMove(idx)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                    >
                      ×
                    </button>
                  </div>
                  <textarea
                    value={move.description}
                    onChange={(e) => updateMove(idx, 'description', e.target.value)}
                    placeholder="Move description..."
                    rows={2}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Alignment */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Allineamento (optional)
            </label>
            <input
              type="text"
              value={alignment}
              onChange={(e) => setAlignment(e.target.value)}
              placeholder="es. Legale Buono, Caotico Neutrale..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Bonds */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-orange-400">LEGAMI POSSIBILI</label>
              <button
                type="button"
                onClick={() => setBonds([...bonds, ''])}
                className="text-orange-400 hover:text-orange-300 text-sm font-semibold"
              >
                + Aggiungi Legame
              </button>
            </div>
            <p className="text-gray-500 text-xs mb-2">
              Usa [BLU], [ROSSO], [VERDE], [GIALLO] per riferirsi agli altri eroi
            </p>
            <div className="space-y-2">
              {bonds.map((bond, idx) => (
                <div key={idx} className="flex gap-2">
                  <span className="text-gray-500 text-sm w-6 pt-2">{idx + 1}.</span>
                  <input
                    type="text"
                    value={bond}
                    onChange={(e) => {
                      const updated = [...bonds];
                      updated[idx] = e.target.value;
                      setBonds(updated);
                    }}
                    placeholder="es. è il migliore amico di [ROSSO]"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    type="button"
                    onClick={() => setBonds(bonds.filter((_, i) => i !== idx))}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
                  >
                    ×
                  </button>
                </div>
              ))}
              {bonds.length === 0 && (
                <p className="text-gray-500 text-sm italic py-2">
                  Nessun legame definito. Clicca "+ Aggiungi Legame" per iniziare.
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Additional Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this character class..."
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              {isSubmitting ? 'Saving...' : sheet ? 'Update Sheet' : 'Create Sheet'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}
